import { AxiosResponse } from "axios";
import { call, CallEffect, PutEffect, SelectEffect, select, delay } from "redux-saga/effects";
import { Action } from "redux-saga";
import { ReducersTypes } from "@/redux/reducers/Models";
import { automaticApi } from "@/settings/api";
import { jwtDecode } from "jwt-decode";
import { BulkCreateResult } from "@/pages/register/Models/vehicle";
import environment from "@/settings/environment";

export interface BulkCreateItem {
  line: number;
  plate: string;
}

export interface Fetch {
  items: BulkCreateItem[];
  callback: (success: boolean, result?: BulkCreateResult) => void;
}

export interface BulkCreateVehiclesAction {
  type: string;
  payload: Fetch;
}

const uri = "/internalwebapi/v1/vehicles/import";

const getCustomer = (state: ReducersTypes) => state.customerReducer;

export default function* bulkCreateVehicles({
  payload,
}: BulkCreateVehiclesAction): Generator<
  CallEffect<unknown> | PutEffect<Action> | SelectEffect,
  void,
  AxiosResponse
> {
  try {
    // @ts-expect-error
    const customer: ReducersTypes["customerReducer"] =
      yield select(getCustomer);

    let customerIdentification = "";
    const accountIdentification = customer?.account?.id ?? "";

    if (customer?.accessToken) {
      try {
        const decoded = jwtDecode<{ NRC: string }>(customer.accessToken);
        customerIdentification = decoded?.NRC ?? "";
      } catch {}
    }

    const BATCH_SIZE = 5;
    const BATCH_DELAY_MS = 1000;
    const batches: BulkCreateItem[][] = [];
    for (let i = 0; i < payload.items.length; i += BATCH_SIZE) {
      batches.push(payload.items.slice(i, i + BATCH_SIZE));
    }

    const allErrors: { plate: string; line: number; reason: string }[] = [];
    let lastCode: number = -1;
    let lastMessage = "";

    for (let idx = 0; idx < batches.length; idx++) {
      if (idx > 0) yield delay(BATCH_DELAY_MS);
      const batch = batches[idx];
      const body = {
        customerIdentification,
        accountIdentification,
        companyIdentification: environment.api.companyIdentification,
        channel: environment.api.automaticChannel,
        plates: batch.map(({ line, plate }) => ({ line, plate })),
      };

      const response: AxiosResponse = yield call(automaticApi.post, uri, body);
      const responseData =
        response?.data?.data ?? response?.data?.dados ?? response?.data;

      lastCode = responseData?.code ?? -1;
      lastMessage = responseData?.message ?? "";

      const batchErrors = responseData?.errors ?? [];
      allErrors.push(...(Array.isArray(batchErrors) ? batchErrors : []));
    }

    const result: BulkCreateResult = {
      code: lastCode as BulkCreateResult["code"],
      message: lastMessage,
      errors: allErrors,
    };
    yield call(
      payload.callback as (s: boolean, r?: BulkCreateResult) => void,
      true,
      result,
    );
  } catch {
    yield call(payload.callback as (s: boolean) => void, false);
  }
}
